import { type ReactNode, useMemo, useState } from "react"
import { IconCalendarTime, IconLoader2, IconRefresh } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAppDispatch, useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import { bookingSeasonApi, type BookingSeasonSnapshot, useGetBookingSeasonQuery } from "./bookingSeasonApi"
import { SeasonForm } from "./components/SeasonForm"
import { SeasonStatusCard } from "./components/SeasonStatusCard"
import { formFromSnapshot, sameForm, type SeasonFormState } from "./lib/seasonForm"

export default function BookingSeasonPage() {
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.permissions)
  // Only people who can change the season open this page — Scheduling Manager, not Appointment
  // Viewer. Viewers still see the season where it affects them, in the calendar's banner.
  const canManage = hasPermission(permissions, "appointments:manage-booking-season")

  const { data: season, isLoading, isFetching, error, refetch } = useGetBookingSeasonQuery(undefined, { skip: !canManage })

  // The admin's unsaved edits, or null while the form simply shows the current season. Keeping only
  // the edits means a refetch (another admin's save, a refresh) flows into an untouched form but never
  // overwrites one being edited.
  const [draft, setDraft] = useState<SeasonFormState | null>(null)
  const saved = useMemo(() => (season ? formFromSnapshot(season) : null), [season])
  const form = draft ?? saved

  // Show the season a save or close-now answered with straight away, rather than the old one until the refetch lands.
  function followSeason(next: BookingSeasonSnapshot) {
    dispatch(bookingSeasonApi.util.upsertQueryData("getBookingSeason", undefined, next))
    setDraft(null)
  }

  if (!canManage) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Header />
        <p className="text-muted-foreground">You do not have permission to view the booking season.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <Header>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          {isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
          Refresh
        </Button>
      </Header>

      <p className="text-muted-foreground max-w-3xl">
        When the public website and phone line can book new appointments. Outside the season they are turned away
        with the closed messages below. Managing an existing appointment always works, and staff can always book.
        All times are Pacific.
      </p>

      {isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : error || !season || !form || !saved ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">
            {(error as { status?: number } | undefined)?.status === 403
              ? "You do not have permission to view the booking season."
              : "Could not load the booking season."}
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          <SeasonStatusCard season={season} onClosed={followSeason} />

          <SeasonForm
            form={form}
            onChange={(next) => setDraft(sameForm(next, saved) ? null : next)}
            unchanged={draft === null}
            onReset={() => setDraft(null)}
            onSaved={followSeason}
          />
        </>
      )}
    </div>
  )
}

function Header({ children }: { children?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <IconCalendarTime className="size-6" />
        <h1 className="text-2xl font-bold">Booking Season</h1>
      </div>
      {children}
    </div>
  )
}
